import { useMemo, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { slotPositions, theme } from './constants';

const useIndicators = ({
    selectedSlot,
    isOptionsOpen,
    hoveredInnerVariant,
    config,
    hoveredSlot,
    indicatorsRef,
    dotRefs,
    lerpTrackers,
    updateLinesRef,
    indicatorContainerRef,
    camera,
    renderer
}) => {
    const indicators = useMemo(() => {
        const isInternalOpen = selectedSlot === 'inner_components' && isOptionsOpen;
        const currentIndicators = [];

        if (isInternalOpen) {
            if (hoveredInnerVariant && slotPositions[hoveredInnerVariant]) {
                currentIndicators.push({ id: hoveredInnerVariant, color: 'rgb(255, 215, 0)' });
            } else {
                config.inner_components.forEach(item => {
                    if (slotPositions[item.variant]) currentIndicators.push({ id: item.variant, color: theme.warning });
                });
            }
        } else {
            const activeDotTarget = hoveredSlot || selectedSlot;
            if (activeDotTarget && slotPositions[activeDotTarget]) {
                currentIndicators.push({ id: activeDotTarget, color: 'rgb(0, 40, 70)' });
            }
        }
        return currentIndicators;
    }, [selectedSlot, isOptionsOpen, hoveredInnerVariant, config, hoveredSlot]);

    useEffect(() => {
        indicatorsRef.current = indicators;
    }, [indicators, indicatorsRef]);

    const tempTargetVec = useMemo(() => new THREE.Vector3(), []);

    useEffect(() => {
        updateLinesRef.current = (cam) => {
            if (!cam || !indicatorContainerRef.current) return;

            indicatorsRef.current.forEach((ind, index) => {
                const cached = dotRefs.current[index];
                if (!cached || !slotPositions[ind.id]) return;

                tempTargetVec.set(slotPositions[ind.id].x, slotPositions[ind.id].y, slotPositions[ind.id].z);

                if (!lerpTrackers.current[index]) {
                    lerpTrackers.current[index] = tempTargetVec.clone();
                }

                lerpTrackers.current[index].lerp(tempTargetVec, 0.10);

                const v = tempTargetVec.copy(lerpTrackers.current[index]).project(cam);
                const x = (v.x * 0.5 + 0.5) * window.innerWidth;
                const y = (-v.y * 0.5 + 0.5) * window.innerHeight;

                cached.group.setAttribute('transform', `translate(${x}, ${y})`);
                cached.main.setAttribute('fill', ind.color);
                cached.ping.setAttribute('fill', ind.color);
            });
        };
    }, [dotRefs, lerpTrackers, tempTargetVec, updateLinesRef, indicatorContainerRef, indicatorsRef]);

    const handleResize = useCallback(() => {
        if (!camera || !renderer) return;
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
    }, [camera, renderer]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    return indicators;
};

export default useIndicators;